/**
 * Admin URL obfuscation.
 *
 * Encodes admin paths so the browser URL bar shows `/a/xYz...` instead of
 * `/admin/integrations`. Uses a simple XOR cipher with the app key.
 *
 * Only active on the admin portal domain (dev.rentpay.me).
 * In dev/preview, original paths are used for easier debugging.
 */
import { isAdminPortal } from "@/lib/domain";

const KEY = "18 oct 2001";

// XOR each byte of the path with the key (repeating)
function xorCipher(text) {
  const out = [];
  for (let i = 0; i < text.length; i++) {
    out.push(text.charCodeAt(i) ^ KEY.charCodeAt(i % KEY.length));
  }
  return out;
}

// base64url encode (URL-safe, no padding)
function toBase64Url(bytes) {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// base64url decode
function fromBase64Url(str) {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded);
  return Array.from(binary, (c) => c.charCodeAt(0));
}

/**
 * Encode an admin path for the URL bar.
 *   "/admin/integrations" → "/a/eJ0xK2..."
 *
 * On non-admin domains (dev/preview), returns the original path.
 */
export function encodeAdminPath(path) {
  if (!isAdminPortal()) return path;
  // Only encode /admin/* paths
  if (!path.startsWith("/admin")) return path;
  const encoded = toBase64Url(xorCipher(path));
  return `/a/${encoded}`;
}

/**
 * Decode an obfuscated admin URL back to the real path.
 *   "/a/eJ0xK2..." → "/admin/integrations"
 */
export function decodeAdminPath(obfuscatedPath) {
  if (!obfuscatedPath.startsWith("/a/")) return obfuscatedPath;
  const encoded = obfuscatedPath.slice(3); // remove "/a/"
  try {
    const bytes = fromBase64Url(encoded);
    const decoded = xorCipher(String.fromCharCode(...bytes));
    return String.fromCharCode(...decoded);
  } catch {
    return "/admin"; // fallback
  }
}

/**
 * Static map of all admin routes → their encoded equivalents.
 * Pre-computed for use in React Router route definitions.
 */
const ADMIN_PATHS = [
  "/admin",
  "/admin/approvals",
  "/admin/all-txns",
  "/admin/beneficiary-approvals",
  "/admin/kyc",
  "/admin/retailers",
  "/admin/users",
  "/admin/packages",
  "/admin/integrations",
  "/admin/feature-matrix",
  "/admin/roles",
  "/admin/sub-admins",
  "/admin/customers",
  "/admin/customer-kyc",
  "/admin/customer-wallet-rules",
  "/admin/webhook-events",
  "/admin/payment-links",
  "/admin/api-tester",
  "/admin/sdk-clients",
  "/admin/platform",
  "/admin/domains",
  "/admin/payment-modes",
];

// Build a lookup: encoded → real path
const _decodeLookup = {};
if (typeof window !== "undefined" && isAdminPortal()) {
  ADMIN_PATHS.forEach((p) => {
    const enc = encodeAdminPath(p);
    _decodeLookup[enc] = p;
  });
}

/**
 * Get the real admin path from an encoded URL path.
 * Used by the catch-all route renderer.
 */
export function resolveAdminPath(locationPath) {
  // Direct match on encoded path
  if (_decodeLookup[locationPath]) return _decodeLookup[locationPath];
  // Try decoding dynamically (for paths not in the static list)
  if (locationPath.startsWith("/a/")) return decodeAdminPath(locationPath);
  return locationPath;
}

/**
 * Check if we should use obfuscated admin routes.
 */
export function shouldObfuscateRoutes() {
  return isAdminPortal();
}
